import prepaidCardImageQueries from '@apis/queries/prepaidCardImageQueries';
import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import Divider from '@components/Divider';
import Header from '@components/Header';
import PrepaidCardNotes from '@components/prepaid/PrepaidCardNotes';
import useBeginCheckoutOnce from '@hooks/analytics/useBeginCheckoutOnce';
import useCurrency from '@hooks/item/useCurrency';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useQuery } from '@tanstack/react-query';
import { prepaidCardCheckoutToGtmFormat } from '@utils/gtm';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CardInfo from './components/CardInfo';
import PaymentInfo from './components/PaymentInfo';
import PurchaseAgreements from './components/PurchaseAgreements';
import PurchaseButton from './components/PurchaseButton';
import Summary from './components/Summary';
import useInitialValues from './hooks/useInitialValues';

interface Params {
    prepaidCardImageId: string;
    cardName?: string;
    amount?: number;
}

declare module '@stackflow/config' {
    interface Register {
        PrepaidCardImageActivity: Params;
    }
}

const PrepaidCardImageActivity: ActivityComponentType<'PrepaidCardImageActivity'> = ({ params }) => {
    const { t } = useTranslation('prepaidCard');
    const { prepaidCardImageId, cardName, amount } = params;
    useInitialValues({ cardName, amount });
    const { data: image } = useQuery(prepaidCardImageQueries.detail(prepaidCardImageId));
    const currency = useCurrency();
    // 결제 실패·취소 후 복원 진입(amount 전달)은 발화하지 않는다
    const beginCheckout = useMemo(() => (
        image && amount === undefined
            ? prepaidCardCheckoutToGtmFormat({
                id: image.id, name: image.title, currency, source: 'PREPAID_CARD_PURCHASE',
            })
            : undefined
    ), [image, amount, currency]);
    useBeginCheckoutOnce(beginCheckout);

    return (
        <AppScreen>
            <CtaLayout header={<Header title={t('image.title')} />}
                footer={<PurchaseButton prepaidCardImageId={prepaidCardImageId} />}>
                <Column fullWidth>
                    <Summary prepaidCardImageId={prepaidCardImageId} />
                    <Divider />
                    <CardInfo />
                    <Divider/>
                    <PaymentInfo />
                    <Divider/>
                    <PrepaidCardNotes types={['USAGE']} noMargin />
                    <PurchaseAgreements />
                </Column>
            </CtaLayout>
        </AppScreen>
    );
};

export default PrepaidCardImageActivity;
